import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import type { RowModel } from '@tanstack/react-table';
import type { RedirectedProductListEntry } from 'contracts/redirected-products';
import { useAuth } from 'hooks';
import {
  useFetchRedirectedProducts,
  useRevertRedirectedProducts,
  useUpdateRedirectedProduct,
} from 'hooks/api/redirected-products';
import { useRedirectedProductsSearchParams } from 'hooks/store/redirected-products';
import { useOffsetTop } from 'hooks/ui';
import { Scaffold } from 'layouts';
import { notify } from 'services';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTable, type DataTableRef } from 'components/ui/DataTable';

import { TabbedNavigation, Indicators } from '../shared-components';
import { useRedirectedProductColumns } from './hooks';
import {
  RedirectedProductsFilter,
  RevertRedirectedProductsDrawer,
  type RevertRedirectedProductsDrawerRef,
  UpdateRedirectedProductSapStatusDrawer,
  type UpdateRedirectedProductSapStatusDrawerRef,
} from './components';

const NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_SUCCESS = false;

export const RedirectedProductsPage = () => {
  const navigate = useNavigate();
  const { filters, updateFilters, clearState } = useRedirectedProductsSearchParams();
  const { fetchRedirectedProducts } = useFetchRedirectedProducts();
  const { revertRedirectedProducts } = useRevertRedirectedProducts();
  const { updateRedirectedProduct } = useUpdateRedirectedProduct();
  const { profile } = useAuth();

  const datatableWrapperRef = React.useRef<HTMLDivElement>(null);
  const revertRedirectedProductsDrawerRef = React.useRef<RevertRedirectedProductsDrawerRef>(null);
  const updateRedirectedProductSapStatusDrawerRef = React.useRef<UpdateRedirectedProductSapStatusDrawerRef>(null);
  const dataTableRef = React.useRef<DataTableRef>(null);

  const datatableWrapperOffsetTop = useOffsetTop({
    ref: datatableWrapperRef,
    updateInterval: 1000,
  });

  const isAdminOrPlanner = React.useMemo(() => {
    if (!profile?.type) return false;

    return ['admin', 'planner'].includes(profile.type);
  }, [profile?.type]);

  const datatableWrapperStyle = React.useMemo((): React.CSSProperties => {
    return {
      minHeight: '100px',
      maxHeight: `calc(100vh - ${datatableWrapperOffsetTop + 150}px)`,
    };
  }, [datatableWrapperOffsetTop]);

  const columns = useRedirectedProductColumns({
    onUpdateSapStatus: updateRedirectedProductSapStatusDrawerRef.current?.open,
    showSelectionColumn: isAdminOrPlanner,
    showActionsDropdown: isAdminOrPlanner,
  });

  const queryClient = useQueryClient();

  const productsQuery = useQuery({
    queryKey: ['fetch-redirected-products', filters],
    queryFn: () => fetchRedirectedProducts({ filters }),
    refetchOnWindowFocus: false,
  });

  const revertRedirectedProductsMutation = useMutation({
    mutationFn: revertRedirectedProducts,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-redirected-products']);
      notify('success', 'Produtos revertidos com sucesso!');
      if (NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/produtos-bloqueados');
      }
    },
    onError: () => {
      notify('error', 'Erro ao reverter produtos.');
    },
    onSettled: () => {
      revertRedirectedProductsDrawerRef.current?.close();
      dataTableRef.current?.clearSelection();
    },
  });

  const updateRedirectedProductMutation = useMutation({
    mutationFn: updateRedirectedProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-redirected-products']);
      notify('success', 'Produto atualizado com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao atualizar produto.');
    },
    onSettled: () => {
      updateRedirectedProductSapStatusDrawerRef.current?.close();
    },
  });

  const onSelectRows = React.useCallback((rowModel: RowModel<RedirectedProductListEntry>) => {
    const selectedProducts = rowModel.flatRows.map(i => i.original);
    revertRedirectedProductsDrawerRef.current?.open(selectedProducts);
  }, []);

  return (
    <Scaffold>
      <TabbedNavigation />
      <PageHeaderForFilters headerTitle={
        <div className={'w-full font-GilroySemibold'}>Filtro</div>
      } className={'mb-4'}>
        <RedirectedProductsFilter
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <Indicators
        indicators={productsQuery?.data?.indicators}
        loading={productsQuery.isLoading}
        className={'mb-4'}
      />
      <RevertRedirectedProductsDrawer
        ref={revertRedirectedProductsDrawerRef}
        onSubmit={revertRedirectedProductsMutation.mutate}
        loading={revertRedirectedProductsMutation.isLoading}
      />
      <UpdateRedirectedProductSapStatusDrawer
        ref={updateRedirectedProductSapStatusDrawerRef}
        onSubmit={updateRedirectedProductMutation.mutate}
        loading={updateRedirectedProductMutation.isLoading}
      />
      <div ref={datatableWrapperRef}>
        <DataTable
          ref={dataTableRef}
          data={productsQuery?.data?.redirectedProducts ?? []}
          isLoading={productsQuery.isLoading}
          columns={columns}
          tableWrapperStyle={datatableWrapperStyle}
          leftPinnedColumns={['Selection', 'Planta']}
          rightPinnedColumns={['SapIndicator', 'Actions']}
          selectionAction={isAdminOrPlanner ? onSelectRows : undefined}
          selectionActionLabel={'Reverter'}
        />
      </div>
    </Scaffold>
  );
};

